import React, { useState } from 'react';
import { X, Plus, Trash2, Pencil, Users, User } from 'lucide-react';
import { CannedReply, CannedReplyInput } from '../../../types/models/whatsapp';

interface CannedReplyManagerProps {
  open: boolean;
  replies: CannedReply[];
  agentId: string | null;
  onClose: () => void;
  onCreate: (input: CannedReplyInput) => Promise<void>;
  onUpdate: (id: string, input: Partial<CannedReplyInput>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const EMPTY_FORM: CannedReplyInput = {
  shortcut: '',
  title: '',
  body: '',
  category: 'General',
  scope: 'team'
};

/** The placeholders `expandReplyBody` understands, offered as one-click inserts. */
const VARIABLES = ['contact.name', 'contact.phone', 'agent.name'];

const CannedReplyManager: React.FC<CannedReplyManagerProps> = ({
  open,
  replies,
  agentId,
  onClose,
  onCreate,
  onUpdate,
  onDelete
}) => {
  const [form, setForm] = useState<CannedReplyInput>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!form.shortcut.trim() || !form.title.trim() || !form.body.trim()) {
      setError('Shortcut, title and body are all required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await onUpdate(editingId, form);
      } else {
        await onCreate(form);
      }
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (reply: CannedReply) => {
    setEditingId(reply.id);
    setError(null);
    setForm({
      shortcut: reply.shortcut,
      title: reply.title,
      body: reply.body,
      category: reply.category,
      scope: reply.scope
    });
  };

  const handleDelete = async (reply: CannedReply) => {
    setError(null);
    try {
      await onDelete(reply.id);
      if (editingId === reply.id) resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const insertVariable = (variable: string) =>
    setForm((current) => ({
      ...current,
      body: `${current.body}{{${variable}}}`
    }));

  /** A personal reply can only be edited by its author. */
  const canEdit = (reply: CannedReply) =>
    reply.scope === 'team' || reply.createdBy === agentId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">
            Canned replies
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 md:grid-cols-2">
          {/* Editor */}
          <div className="space-y-2">
            <h3 className="text-xs font-medium text-gray-700">
              {editingId ? 'Edit reply' : 'New reply'}
            </h3>

            <div className="flex gap-2">
              <span className="flex items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-2 font-mono text-xs text-gray-500">
                /
              </span>
              <input
                type="text"
                value={form.shortcut}
                onChange={(e) => setForm({ ...form, shortcut: e.target.value })}
                placeholder="shortcut"
                className="-ml-2 min-w-0 flex-1 rounded-r-lg border-gray-300 font-mono text-xs focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Title (e.g. Order status enquiry)"
              className="w-full rounded-lg border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500"
            />

            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={5}
              placeholder="Message body…"
              className="w-full resize-none rounded-lg border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500"
            />

            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[11px] text-gray-500">Insert:</span>
              {VARIABLES.map((variable) => (
                <button
                  key={variable}
                  type="button"
                  onClick={() => insertVariable(variable)}
                  className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-600 hover:bg-gray-200"
                >
                  {`{{${variable}}}`}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="Category"
                className="min-w-0 flex-1 rounded-lg border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500"
              />
              <select
                value={form.scope}
                onChange={(e) =>
                  setForm({ ...form, scope: e.target.value as 'team' | 'personal' })
                }
                className="rounded-lg border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="team">Team</option>
                <option value="personal">Personal</option>
              </select>
            </div>

            {error && <p className="text-[11px] text-danger-600">{error}</p>}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" />
                {saving ? 'Saving…' : editingId ? 'Update' : 'Create'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Existing replies */}
          <div className="space-y-1.5">
            <h3 className="text-xs font-medium text-gray-700">
              Saved ({replies.length})
            </h3>

            {replies.length === 0 && (
              <p className="py-6 text-center text-xs text-gray-400">
                No canned replies yet. Create one to use it with <code>/</code>.
              </p>
            )}

            {replies.map((reply) => (
              <div
                key={reply.id}
                className="rounded-lg border border-gray-200 p-2"
              >
                <div className="flex items-start gap-2">
                  <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-600">
                    /{reply.shortcut}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-gray-900">
                      {reply.title}
                    </p>
                    <p className="truncate text-[11px] text-gray-500">
                      {reply.body}
                    </p>
                  </div>
                  {reply.scope === 'team' ? (
                    <Users className="h-3 w-3 shrink-0 text-gray-400" aria-label="Team" />
                  ) : (
                    <User className="h-3 w-3 shrink-0 text-gray-400" aria-label="Personal" />
                  )}
                  {canEdit(reply) && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleEdit(reply)}
                        className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100"
                        aria-label={`Edit ${reply.title}`}
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(reply)}
                        className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-danger-50 hover:text-danger-600"
                        aria-label={`Delete ${reply.title}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CannedReplyManager;
